// Account list + detail API — FE-038 / FE-039 / FE-040.
//
// Talks to the canonical ``/api/v1/accounts/`` REST surface (BE-052).
// The detail endpoint returns the full editable record (employee
// counts, addresses, EIN, BOR date, notes), so the edit form can
// hydrate every field — without that, an unedited field PATCHes back
// as an empty string and silently overwrites the persisted value.

import { api, ApiError } from "@/api/client";
import type { components, operations } from "@/api/schema.gen";

import type {
  AccountDetail,
  AccountListFilters,
  AccountListQuery,
  AccountListResponse,
  AccountListRow,
  AccountMutationResponse,
  CreateAccountInput,
  UpdateAccountInput,
} from "./types";
import { AccountNotFoundError } from "./types";


interface RawAccountRow {
  account_id: string;
  legal_name: string;
  dba_name?: string | null;
  status?: string | null;
  industry?: string | null;
  employee_count_total?: number | null;
  employee_count_eligible?: number | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  current_plan_year_id?: string | null;
  current_plan_year?: { plan_year_id: string } | null;
  renewal_date?: string | null;
  open_commitments?: number | null;
  overdue_commitments?: number | null;
  blocked_commitments?: number | null;
  completion_rate?: number | null;
  on_time_percent?: number | null;
  risk_bucket?: AccountListRow["risk_bucket"];
  updated_at?: string | null;
  // Accounts-01 — segmentation scalars on list rows.
  primary_location_id?: string | null;
  naics_sector?: string | null;
  naics_code?: string | null;
  risk_class?: AccountListRow["risk_class"];
}


interface RawAccountDetail extends RawAccountRow {
  primary_location?: {
    location_id: string;
    name: string;
    city?: string | null;
    state_code?: string | null;
    status?: string | null;
  } | null;
  naics_title?: string | null;
  account_tags?: Array<{
    tag_id: string;
    name: string;
    slug: string;
  }>;
  client_team?: AccountTeamMember[];
  ein?: string | null;
  bor_signed_at?: string | null;
  primary_address_line1?: string | null;
  primary_address_line2?: string | null;
  primary_address_city?: string | null;
  primary_address_state?: string | null;
  primary_address_zip?: string | null;
  notes?: string | null;
  notes_document_id?: string | null;
  recent_commitments?: Array<{
    commitment_id: string;
    title: string;
    status: string;
    due_date_time?: string | null;
    is_blocked?: boolean;
  }>;
  recent_touchpoints?: Array<{
    touchpoint_id: string;
    kind: string;
    occurred_at: string;
    body_excerpt: string;
  }>;
  linked_files?: Array<{
    id: string;
    filename: string;
    size_bytes?: number | null;
    uploaded_at?: string | null;
  }>;
}


export async function fetchAccountList(
  query: AccountListQuery,
  opts?: { signal?: AbortSignal },
): Promise<AccountListResponse> {
  const params = buildListParams(query);
  const path = `/api/v1/accounts/${params ? `?${params}` : ""}`;
  const response = await api.get<{
    rows: RawAccountRow[]; total: number;
  }>(path, opts);
  return {
    rows: response.rows.map(mapRowToAccountListRow),
    total: response.total,
  };
}


function buildListParams(query: AccountListQuery): string {
  const sp = new URLSearchParams();
  const f: AccountListFilters = query.filters;
  if (f.id) sp.set("id", f.id);
  if (f.search) sp.set("search", f.search);
  if (f.status) sp.set("status", f.status);
  if (f.risk_level) sp.set("risk_level", f.risk_level);
  if (f.has_overdue !== undefined) {
    sp.set("has_overdue", f.has_overdue ? "true" : "false");
  }
  if (f.going_cold !== undefined) {
    sp.set("going_cold", f.going_cold ? "true" : "false");
  }
  if (f.renewal_month) sp.set("renewal_month", f.renewal_month);
  if (f.renewal_status) sp.set("renewal_status", f.renewal_status);
  if (f.renewal_attention !== undefined) {
    sp.set("renewal_attention", f.renewal_attention ? "true" : "false");
  }
  if (f.primary_location_id) {
    sp.set("primary_location_id", f.primary_location_id);
  }
  if (f.naics_sector) sp.set("naics_sector", f.naics_sector);
  if (f.naics_code) sp.set("naics_code", f.naics_code);
  if (f.risk_class) sp.set("risk_class", f.risk_class);
  if (f.account_tag_id) sp.set("account_tag_id", f.account_tag_id);
  if (f.account_tag_slug) {
    sp.set("account_tag_slug", f.account_tag_slug);
  }
  sp.set("sort_by", query.sortBy);
  sp.set("sort_dir", query.sortDir);
  sp.set("limit", String(query.limit));
  sp.set("offset", String(query.offset));
  return sp.toString();
}


function mapRowToAccountListRow(row: RawAccountRow): AccountListRow {
  const status = row.status === "archived" ? "archived" : "active";
  return {
    account_id: row.account_id,
    legal_name: row.legal_name,
    dba_name: row.dba_name ?? null,
    status,
    industry: row.industry ?? null,
    employee_count_total: row.employee_count_total ?? null,
    employee_count_eligible: row.employee_count_eligible ?? null,
    primary_contact_name: row.primary_contact_name ?? null,
    primary_contact_email: row.primary_contact_email ?? null,
    current_plan_year_id:
      row.current_plan_year_id
        ?? row.current_plan_year?.plan_year_id
        ?? null,
    renewal_date: row.renewal_date ?? null,
    open_commitments: row.open_commitments ?? 0,
    overdue_commitments: row.overdue_commitments ?? 0,
    blocked_commitments: row.blocked_commitments ?? 0,
    completion_rate: row.completion_rate ?? 0,
    on_time_percent: row.on_time_percent ?? 0,
    risk_bucket: row.risk_bucket ?? "low",
    updated_at: row.updated_at ?? null,
    primary_location_id: row.primary_location_id ?? null,
    naics_sector: row.naics_sector ?? null,
    naics_code: row.naics_code ?? null,
    risk_class: row.risk_class ?? null,
  };
}


// ── fetchAccountDetail ──────────────────────────────────────────────────


/**
 * Fetch a single Account's full detail row. Reads the canonical
 * ``GET /api/v1/accounts/{id}`` endpoint so every editable field is
 * available to the edit form (employee counts, address, EIN, BOR
 * date, notes). Throws :class:`AccountNotFoundError` on a 404 so the
 * page can render the dedicated not-found view without conflating
 * 404 with a transient network failure.
 */
export async function fetchAccountDetail(
  accountId: string,
  opts?: { signal?: AbortSignal },
): Promise<AccountDetail> {
  try {
    const raw = await api.get<RawAccountDetail>(
      `/api/v1/accounts/${encodeURIComponent(accountId)}`,
      opts,
    );
    return mapRawToDetail(raw);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      throw new AccountNotFoundError(accountId);
    }
    throw err;
  }
}


function mapRawToDetail(raw: RawAccountDetail): AccountDetail {
  const base = mapRowToAccountListRow(raw);
  return {
    ...base,
    ein: raw.ein ?? null,
    bor_signed_at: raw.bor_signed_at ?? null,
    bor_date: raw.bor_signed_at ?? null,
    primary_address_line1: raw.primary_address_line1 ?? null,
    primary_address_line2: raw.primary_address_line2 ?? null,
    primary_address_city: raw.primary_address_city ?? null,
    primary_address_state: raw.primary_address_state ?? null,
    primary_address_zip: raw.primary_address_zip ?? null,
    notes: raw.notes ?? null,
    notes_document_id: raw.notes_document_id ?? null,
    recent_commitments: (raw.recent_commitments ?? []).map((c) => ({
      id: c.commitment_id,
      title: c.title,
      status: c.status,
      due_date_time: c.due_date_time ?? null,
      is_blocked: Boolean(c.is_blocked),
    })),
    recent_touchpoints: (raw.recent_touchpoints ?? []).map((t) => ({
      id: t.touchpoint_id,
      kind: t.kind,
      body: t.body_excerpt ?? "",
      occurred_at: t.occurred_at,
    })),
    linked_files: (raw.linked_files ?? []).map((f) => ({
      id: f.id,
      filename: f.filename,
      size_bytes: f.size_bytes ?? null,
      uploaded_at: f.uploaded_at ?? null,
    })),
    source: "accounts_endpoint",
    primary_location: raw.primary_location
      ? {
          location_id: raw.primary_location.location_id,
          name: raw.primary_location.name,
          city: raw.primary_location.city ?? null,
          state_code: raw.primary_location.state_code ?? null,
          status:
            raw.primary_location.status === "inactive"
              ? "inactive"
              : "active",
        }
      : null,
    naics_title: raw.naics_title ?? null,
    account_tags: (raw.account_tags ?? []).map((t) => ({
      tag_id: t.tag_id,
      name: t.name,
      slug: t.slug,
    })),
    client_team: raw.client_team ?? [],
  };
}


// ── createAccount / updateAccount (FE-040) ──────────────────────────────
//
// Backend: BE-052 owns the canonical accounts endpoint. Until then
// these calls surface 404 / 501 as a regular ApiError that the
// form's mutation handler renders as a safe message.


export function createAccount(
  input: CreateAccountInput,
): Promise<AccountMutationResponse> {
  return api.post<AccountMutationResponse>("/api/v1/accounts/", input);
}


export function updateAccount(
  accountId: string,
  input: UpdateAccountInput,
): Promise<AccountMutationResponse> {
  return api.patch<AccountMutationResponse>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}`,
    input,
  );
}


// ── Accounts-01 — NAICS reference + tag inventory ─────────────────────────


import type {
  AccountContact,
  AccountContactInput,
  AccountContactListResponse,
  AccountContactPatch,
  AccountTagSummary,
  AccountTeamListResponse,
  AccountTeamMember,
  AddAccountTeamMemberInput,
  NaicsSearchResult,
  NaicsSectorOption,
} from "./types";


export function fetchNaicsSectors(opts?: {
  signal?: AbortSignal;
}): Promise<{ sectors: NaicsSectorOption[] }> {
  return api.get<{ sectors: NaicsSectorOption[] }>(
    "/api/v1/naics/sectors", opts,
  );
}


export function searchNaicsCodes(
  params: { q: string; sector?: string; limit?: number },
  opts?: { signal?: AbortSignal },
): Promise<{ results: NaicsSearchResult[] }> {
  const sp = new URLSearchParams();
  sp.set("q", params.q);
  if (params.sector) sp.set("sector", params.sector);
  if (params.limit) sp.set("limit", String(params.limit));
  return api.get<{ results: NaicsSearchResult[] }>(
    `/api/v1/naics/search?${sp.toString()}`, opts,
  );
}


export function fetchAccountTags(opts?: {
  signal?: AbortSignal;
}): Promise<{ rows: AccountTagSummary[]; total: number }> {
  return api.get<{ rows: AccountTagSummary[]; total: number }>(
    "/api/v1/account-tags/", opts,
  );
}


export function createAccountTag(
  name: string,
): Promise<AccountTagSummary> {
  return api.post<AccountTagSummary>("/api/v1/account-tags/", { name });
}


// ── Accounts-Teams-01 — Client team ──────────────────────────────────────


function _accountTeamBase(accountId: string): string {
  return `/api/v1/accounts/${encodeURIComponent(accountId)}/team-members/`;
}


export function fetchAccountTeam(
  accountId: string,
  opts?: { signal?: AbortSignal },
): Promise<AccountTeamListResponse> {
  return api.get<AccountTeamListResponse>(_accountTeamBase(accountId), opts);
}


export function addAccountTeamMember(
  accountId: string, input: AddAccountTeamMemberInput,
): Promise<AccountTeamMember> {
  return api.post<AccountTeamMember>(_accountTeamBase(accountId), input);
}


export function patchAccountTeamMember(
  accountId: string, assignmentId: string,
  values: { is_account_lead?: boolean },
): Promise<AccountTeamMember> {
  return api.patch<AccountTeamMember>(
    `${_accountTeamBase(accountId)}${encodeURIComponent(assignmentId)}`,
    values,
  );
}


export function removeAccountTeamMember(
  accountId: string, assignmentId: string,
): Promise<{ id: string; removed: boolean }> {
  return api.post<{ id: string; removed: boolean }>(
    `${_accountTeamBase(accountId)}${encodeURIComponent(assignmentId)}/remove`,
    {},
  );
}


// ── Accounts-Contacts-01 — Account contacts ─────────────────────────────


function _accountContactsBase(accountId: string): string {
  return `/api/v1/accounts/${encodeURIComponent(accountId)}/contacts/`;
}


export function fetchAccountContacts(
  accountId: string,
  opts?: { signal?: AbortSignal },
): Promise<AccountContactListResponse> {
  return api.get<AccountContactListResponse>(
    _accountContactsBase(accountId), opts,
  );
}


export function createAccountContact(
  accountId: string, input: AccountContactInput,
): Promise<AccountContact> {
  return api.post<AccountContact>(_accountContactsBase(accountId), input);
}


export function updateAccountContact(
  accountId: string, contactId: string, input: AccountContactPatch,
): Promise<AccountContact> {
  return api.patch<AccountContact>(
    `${_accountContactsBase(accountId)}${encodeURIComponent(contactId)}`,
    input,
  );
}


export function deleteAccountContact(
  accountId: string, contactId: string,
): Promise<{ id: string; deleted: boolean }> {
  return api.post<{ id: string; deleted: boolean }>(
    `${_accountContactsBase(accountId)}${encodeURIComponent(contactId)}/delete`,
    {},
  );
}


export function setPrimaryAccountContact(
  accountId: string, contactId: string,
): Promise<AccountContact> {
  return api.post<AccountContact>(
    `${_accountContactsBase(accountId)}${encodeURIComponent(contactId)}/set-primary`,
    {},
  );
}


export function fetchBrokerLocationsForAccounts(opts?: {
  signal?: AbortSignal;
}): Promise<{ rows: Array<{
  location_id: string;
  name: string;
  city: string | null;
  state_code: string | null;
  status: "active" | "inactive";
}> }> {
  // Reuses the Settings-02 location endpoint; we re-export here so
  // the AccountForm doesn't have to import from a sibling feature.
  return api.get<{ rows: Array<{
    location_id: string;
    name: string;
    city: string | null;
    state_code: string | null;
    status: "active" | "inactive";
  }> }>(
    "/api/v1/broker-locations/", opts,
  );
}


// ── ACQ-004 — link imported employer Entities to Accounts ────────────────
//
// The BeneCRM half of the book import: after a book lands in Shortpay, this
// gives every employer Entity that has no Account one, filed under the office
// that acquired the book. Idempotent server-side, so the onboarding retry can
// re-issue it without re-uploading the book.
//
// Both types are derived from the generated client rather than hand-copied.
// The backend declares this operation's body and response as Ninja Schemas
// (backend/accounts/schemas.py), so the generated contract is the source of
// truth: renaming a field backend-side fails `bun run typecheck` at the call
// below instead of shipping a body the API no longer accepts.

export const ACCOUNTS_LINK_IMPORTED_EMPLOYERS_PATH =
  "/api/v1/accounts/link-imported-employers" as const;

/** The operation's request body, from the generated contract. */
export type LinkImportedEmployersBody =
  operations["accounts_api_account_link_imported_employers"]["requestBody"]["content"]["application/json"];

/** The operation's 200 payload, from the generated contract. */
export type LinkImportedEmployersResult =
  components["schemas"]["LinkImportedEmployersResponse"];

export function linkImportedEmployers(
  primaryLocationId: string,
): Promise<LinkImportedEmployersResult> {
  const body: LinkImportedEmployersBody = {
    primary_location_id: primaryLocationId,
  };
  return api.post<LinkImportedEmployersResult>(
    ACCOUNTS_LINK_IMPORTED_EMPLOYERS_PATH,
    body,
  );
}
